// Billetbetingelser — heywaitr's standard ticket terms, generated per event.
//
// Shown when the venue has not linked its own terms (Restaurant.TicketTermsUrl).
// Everything a guest needs to know who they are dealing with is filled in from
// /tickets/public: the event, when and where, who SELLS the ticket (the venue)
// and who HOLDS the event (an organiser the venue sells on behalf of, or the
// venue itself). Deliberately short and plain: it covers what the law asks a
// guest to be told before buying (who sells, the price, that there is no right
// of withdrawal) and what a guest actually asks (cancellation, entry, data).
//
// /events/:restaurantId/:eventId/betingelser is the per-event page the buy
// button links to; /events/:restaurantId/betingelser (no event) is the venue-wide
// version, kept for links already handed out.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import configData from '../../config.json';
import './styles.css';

const API = (configData && configData.SERVER_URL) || 'http://localhost:5001/api';
export const PRIVACY_URL = 'https://heywaitr.com/privacy-policy/';

const CURRENCY_NAME = { DKK: 'danske kroner', NOK: 'norske kroner', SEK: 'svenske kroner', EUR: 'euro' };

// Danish genitive: "Bingo Bruvs' husregler", not "Bingo Bruvss".
const genitive = (name) => (/[sxz]$/i.test(name) ? `${name}'` : `${name}s`);

const when = (event) => {
  if (!event || !event.date) return '';
  const d = new Date(`${event.date}T00:00:00`);
  const day = isNaN(d.getTime())
    ? event.date
    : d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `${day}${event.start ? ` kl. ${event.start}` : ''}`;
};

export default function Terms() {
  const { restaurantId, eventId } = useParams();
  const [venue, setVenue] = useState({ name: '', address: '', currency: 'DKK' });
  const [event, setEvent] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/tickets/public/${restaurantId}`)
      .then((res) => {
        const c = res.data.currency;
        setVenue({
          name: res.data.venueName || '',
          address: res.data.venueAddress || '',
          currency: (c && typeof c === 'object' ? c.code : c) || 'DKK',
        });
        // A past or unpublished event is not in the public list; the page then
        // falls back to the venue-wide wording rather than showing nothing.
        if (eventId) setEvent((res.data.events || []).find((e) => String(e._id) === String(eventId)) || null);
      })
      .catch(() => {});
  }, [restaurantId, eventId]);

  const seller = venue.name || 'stedet';
  const organizer = event && event.organizer ? event.organizer : null;
  // Whoever answers for the event itself: the organiser when there is one.
  const holder = organizer ? organizer.name : seller;
  const holderContact = organizer && organizer.contact ? ` (${organizer.contact})` : '';
  const where = (event && event.location) || venue.address;

  return (
    <div className="ev-page ev-terms">
      <Link className="ev-back" to={`/events/${restaurantId}`}>← Tilbage til arrangementerne</Link>
      <h1 className="ev-heading">Billetbetingelser</h1>
      <p className="ev-terms-lead">
        {event ? (
          <>
            Gælder for billetter til <b>{event.name}</b>
            {when(event) ? `, ${when(event)}` : ''}
            {where ? `, ${where}` : ''}, købt gennem heywaitr.
          </>
        ) : (
          <>Gælder for billetter til arrangementer hos {seller}, købt gennem heywaitr.</>
        )}
      </p>

      <h2>1. Sælger og arrangør</h2>
      {organizer ? (
        <p>
          Arrangementet afholdes af {organizer.name}{holderContact}. Billetten sælges af {seller}
          {venue.address ? `, ${venue.address},` : ''} på arrangørens vegne. heywaitr formidler salget og
          betalingen på vegne af {seller}.
        </p>
      ) : (
        <p>
          Billetten sælges af {seller}
          {venue.address ? `, ${venue.address},` : ''} som også afholder arrangementet. heywaitr formidler
          salget og betalingen på vegne af {seller}.
        </p>
      )}

      <h2>2. Pris og gebyr</h2>
      <p>
        Alle priser er i {CURRENCY_NAME[venue.currency] || venue.currency} inkl. moms. Ud over billetprisen betaler du et billetgebyr pr.
        billet, som vises tydeligt, før du betaler. Gratis billetter har intet gebyr.
      </p>

      <h2>3. Ingen fortrydelsesret</h2>
      <p>
        Billetter til arrangementer på en bestemt dato er undtaget fra fortrydelsesretten
        (forbrugeraftalelovens § 18, stk. 2, nr. 12). Et køb kan derfor ikke fortrydes, og billetten
        kan ikke returneres.
      </p>

      <h2>4. Aflysning og ændringer</h2>
      <p>
        Bliver arrangementet aflyst, får du hele beløbet inkl. gebyr tilbage på det kort, du betalte
        med. Flyttes arrangementet til en anden dato, gælder billetten til den nye dato. Kan du ikke
        komme på den nye dato, så kontakt {holder}{holderContact}.
      </p>

      <h2>5. Adgang</h2>
      <p>
        Hver billet har sin egen QR-kode, som scannes ved indgangen. En billet kan kun bruges én gang
        — den første, der viser den, kommer ind — så del den ikke med andre. Vis billetten på
        telefonen eller udprintet.
      </p>

      <h2>6. Husregler</h2>
      <p>
        {organizer
          ? `${genitive(organizer.name)} og ${venue.name ? genitive(venue.name) : 'stedets'} husregler`
          : `${venue.name ? genitive(venue.name) : 'Stedets'} husregler`}{' '}
        og eventuelle aldersgrænser gælder. {holder} kan afvise adgang, fx ved beruselse eller
        upassende opførsel, og billetten refunderes ikke i så fald.
      </p>

      <h2>7. Dine oplysninger</h2>
      <p>
        Vi bruger dit navn, telefonnummer og din e-mail til at levere billetten og lukke dig ind. Har
        du sat flueben ved nyheder på e-mail eller SMS, bruger {seller} og heywaitr også oplysningerne
        til det — og du kan altid afmelde dig igen. Læs mere i{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">privatlivspolitikken</a>.
      </p>

      <h2>8. Spørgsmål</h2>
      <p>
        Spørgsmål om arrangementet stilles til {holder}{holderContact}. Spørgsmål om købet og betalingen
        kan stilles til {seller} eller til heywaitr via heywaitr.com.
      </p>
    </div>
  );
}
